import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ChartBar, TrendUp, TrendDown, Calendar, Bell, FileText,
  Receipt, Money, Warning, CheckCircle, Calculator, Download,
  Brain, Sparkle, Lightning, Plus, Trash
} from "@phosphor-icons/react";
import { api, formatCurrency, formatDate, abrirWhatsApp } from "../../utils/api";
import { Button } from "../shared/Button";
import { Loading, EmptyState } from "../shared/EmptyState";
import { Badge } from "../shared/Badge";
import { Modal } from "../shared/Modal";
import { Input, Textarea } from "../shared/Input";

// Mismos tramos que el backend (server.py TRAMOS_IRPF_2025) — estimación
// orientativa, escala general combinada 2025, no reemplaza a un gestor.
const TRAMOS_IRPF_2025 = [
  [12450, 0.19],
  [20200, 0.24],
  [35200, 0.30],
  [60000, 0.37],
  [300000, 0.45],
  [Infinity, 0.47],
];

const calcularIrpfProgresivo = (base) => {
  if (base <= 0) return 0;
  let impuesto = 0;
  let limiteAnterior = 0;
  for (const [limite, tipo] of TRAMOS_IRPF_2025) {
    const tramo = Math.min(base, limite) - limiteAnterior;
    if (tramo <= 0) break;
    impuesto += tramo * tipo;
    limiteAnterior = limite;
  }
  return Math.round(impuesto * 100) / 100;
};

export const GestoraAutomatica = ({ onNavigate, adminToken }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [datosGestora, setDatosGestora] = useState(null);
  const [modalIA, setModalIA] = useState(false);
  const [preguntaIA, setPreguntaIA] = useState("");
  const [respuestaIA, setRespuestaIA] = useState("");
  const [loadingIA, setLoadingIA] = useState(false);
  const [alertas, setAlertas] = useState([]);
  const [loadingAlertas, setLoadingAlertas] = useState(false);
  const [gastos, setGastos] = useState([]);
  const [loadingGastos, setLoadingGastos] = useState(false);
  const [nuevoGasto, setNuevoGasto] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    categoria: "materiales",
    descripcion: "",
    importe: "",
    iva_porcentaje: 21,
    deducible: true,
  });
  const [trimestreSel, setTrimestreSel] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [añoSel, setAñoSel] = useState(new Date().getFullYear());
  const [cobros, setCobros] = useState([]);
  const [loadingCobros, setLoadingCobros] = useState(false);
  const [cobroSeleccionado, setCobroSeleccionado] = useState(null);
  const [nuevoPago, setNuevoPago] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    concepto: "anticipo",
    importe: "",
    metodo: "efectivo",
    notas: "",
  });
  const [costoRealMateriales, setCostoRealMateriales] = useState("");
  const [notaAjusteMateriales, setNotaAjusteMateriales] = useState("");

  useEffect(() => {
    cargarDatosGestora();
  }, []);

  useEffect(() => {
    if (activeTab === "alertas") {
      cargarAlertas();
    }
    if (activeTab === "gastos") {
      cargarGastos();
    }
    if (activeTab === "cobros") {
      cargarCobros();
    }
  }, [activeTab]);

  const cargarCobros = async () => {
    setLoadingCobros(true);
    try {
      const data = await api.get("/admin/cobros", adminToken);
      setCobros(data);
    } catch (e) {
      console.error("Error cargando cobros:", e);
    } finally {
      setLoadingCobros(false);
    }
  };

  const abrirCobro = (cobro) => {
    setCobroSeleccionado(cobro);
    setNuevoPago({
      fecha: new Date().toISOString().slice(0, 10),
      concepto: cobro.total_pagado === 0 ? "anticipo" : (cobro.total_pagado < cobro.anticipo_importe + cobro.mitad_obra_importe ? "mitad_obra" : "final"),
      importe: "",
      metodo: "efectivo",
      notas: "",
    });
  };

  const registrarPago = async () => {
    if (!nuevoPago.importe || Number(nuevoPago.importe) <= 0) return;
    try {
      await api.post("/admin/pagos", {
        presupuesto_id: cobroSeleccionado.presupuesto_id,
        concepto: nuevoPago.concepto,
        importe: Number(nuevoPago.importe),
        fecha: nuevoPago.fecha,
        metodo: nuevoPago.metodo,
        notas: nuevoPago.notas,
      }, adminToken);
      setCobroSeleccionado(null);
      cargarCobros();
    } catch (e) {
      console.error("Error registrando pago:", e);
      alert("Error al registrar el pago");
    }
  };

  const registrarAjusteMateriales = async () => {
    if (!costoRealMateriales || Number(costoRealMateriales) < 0) return;
    try {
      const actualizado = await api.post(
        `/admin/presupuestos/${cobroSeleccionado.presupuesto_id}/ajustar-materiales`,
        { costo_real: Number(costoRealMateriales), nota: notaAjusteMateriales },
        adminToken
      );
      setCobroSeleccionado((prev) => ({
        ...prev,
        total: actualizado.total_final,
        pendiente: round2(actualizado.total_final - (prev.total_pagado || 0)),
        materiales_costo_real: actualizado.materiales_costo_real,
        ahorro_materiales: actualizado.ahorro_materiales,
        ajuste_materiales_aplicado: true,
      }));
      setCostoRealMateriales("");
      setNotaAjusteMateriales("");
      cargarCobros();
    } catch (e) {
      console.error("Error ajustando materiales:", e);
      alert("Error al registrar el ajuste");
    }
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  const avisarAjusteWhatsApp = (cobro) => {
    const ahorro = cobro.ahorro_materiales || 0;
    let mensaje;
    if (ahorro > 0) {
      mensaje = `🏠 *AAGG REFORMAS*\n\nHola ${cobro.cliente_nombre},\n\nQuería contarte algo sobre tu obra (presupuesto *${cobro.numero}*): los materiales terminaron costando ${formatCurrency(cobro.materiales_costo_real)}, menos de lo que habíamos estimado.\n\nComo me parece lo correcto, te descuento esa diferencia — *${formatCurrency(ahorro)}* menos sobre el total.\n\nLa mano de obra queda igual, tal como acordamos. Nuevo total: *${formatCurrency(cobro.total)}*.\n\n¡Gracias por confiar en nosotros! 🙏`;
    } else {
      mensaje = `🏠 *AAGG REFORMAS*\n\nHola ${cobro.cliente_nombre},\n\nTe cuento sobre tu obra (presupuesto *${cobro.numero}*): los materiales costaron ${formatCurrency(cobro.materiales_costo_real)}. Nuevo total: *${formatCurrency(cobro.total)}*.\n\n¡Gracias! 🙏`;
    }
    abrirWhatsApp(cobro.cliente_telefono, mensaje);
  };

  const avisarPagoWhatsApp = (cobro) => {
    let mensaje;
    if (cobro.total_pagado > 0 && cobro.pendiente > 0) {
      mensaje = `🏠 *AAGG REFORMAS*\n\nHola ${cobro.cliente_nombre},\n\n¡Gracias por el pago de ${formatCurrency(cobro.total_pagado)} del presupuesto *${cobro.numero}*! ✅\n\nQueda pendiente: *${formatCurrency(cobro.pendiente)}*\n\nCualquier duda, escribinos por aquí. ¡Gracias! 🙏`;
    } else if (cobro.pendiente > 0) {
      mensaje = `🏠 *AAGG REFORMAS*\n\nHola ${cobro.cliente_nombre},\n\nTe recordamos que queda pendiente de pago *${formatCurrency(cobro.pendiente)}* del presupuesto *${cobro.numero}*.\n\nCualquier duda, contactanos. ¡Gracias! 🙏`;
    } else {
      mensaje = `🏠 *AAGG REFORMAS*\n\nHola ${cobro.cliente_nombre},\n\nTe confirmamos que el presupuesto *${cobro.numero}* está totalmente pagado. ¡Gracias por confiar en nosotros! 🙏`;
    }
    abrirWhatsApp(cobro.cliente_telefono, mensaje);
  };

  const cargarGastos = async () => {
    setLoadingGastos(true);
    try {
      const data = await api.get("/admin/gastos", adminToken);
      setGastos(data);
    } catch (e) {
      console.error("Error cargando gastos:", e);
    } finally {
      setLoadingGastos(false);
    }
  };

  const guardarGasto = async () => {
    if (!nuevoGasto.descripcion || !nuevoGasto.importe) return;
    try {
      await api.post("/admin/gastos", nuevoGasto, adminToken);
      setNuevoGasto({
        fecha: new Date().toISOString().slice(0, 10),
        categoria: "materiales",
        descripcion: "",
        importe: "",
        iva_porcentaje: 21,
        deducible: true,
      });
      cargarGastos();
      cargarDatosGestora();
    } catch (e) {
      console.error("Error guardando gasto:", e);
      alert("Error al guardar el gasto");
    }
  };

  const eliminarGasto = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    try {
      await api.delete(`/admin/gastos/${id}`, adminToken);
      cargarGastos();
      cargarDatosGestora();
    } catch (e) {
      console.error("Error eliminando gasto:", e);
    }
  };

  const cargarDatosGestora = async () => {
    try {
      const datos = await api.get("/gestora/dashboard", adminToken);
      setDatosGestora(datos);
    } catch (e) {
      console.error("Error cargando gestora:", e);
    } finally {
      setLoading(false);
    }
  };
  
  const cargarAlertas = async () => {
    setLoadingAlertas(true);
    try {
      const data = await api.get("/gestora/alertas", adminToken);
      setAlertas(data);
    } catch (e) {
      console.error("Error cargando alertas:", e);
    } finally {
      setLoadingAlertas(false);
    }
  };

  const consultarIA = async () => {
    if (!preguntaIA.trim()) return;
    setLoadingIA(true);
    try {
      const res = await api.post("/gestora/consulta-ia", { pregunta: preguntaIA }, adminToken);
      setRespuestaIA(res.respuesta);
    } catch (e) {
      setRespuestaIA("Error al consultar. Intenta de nuevo.");
    } finally {
      setLoadingIA(false);
    }
  };

  const generarInforme = async (tipo) => {
    try {
      // Hacer POST al endpoint y descargar PDF
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gestora/generar-informe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ tipo })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Informe_${tipo}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert("Error generando informe");
      }
    } catch (e) {
      console.error(e);
      alert("Error generando informe");
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <div className="hero-header" style={{ padding: "24px 20px" }}>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={24} weight="fill" style={{ color: "var(--brand-accent)" }} />
            <Badge variant="accent">IA Automática</Badge>
          </div>
          <div className="text-h2 font-display">Gestora Inteligente</div>
          <div className="text-small" style={{ opacity: 0.8, marginTop: 4 }}>
            Automatiza tu gestión administrativa
          </div>
        </div>
      </div>

      <div className="container" style={{ marginTop: -20, position: "relative", zIndex: 2 }}>
        {/* Tabs */}
        <div className="tabs mb-4">
          {[
            { id: "dashboard", label: "📊 Panel" },
            { id: "fiscal", label: "💰 Fiscal" },
            { id: "gastos", label: "💸 Gastos" },
            { id: "alertas", label: "🔔 Alertas" },
            { id: "facturas", label: "📄 Facturas" },
            { id: "cobros", label: "💵 Cobros" },
            { id: "informes", label: "📈 Informes" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab ${activeTab === tab.id ? "tab-active" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard */}
        {activeTab === "dashboard" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Resumen financiero */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="card card-elevated stat-card" style={{ background: "var(--success)", color: "white" }}>
                <div className="stat-value">{formatCurrency(datosGestora?.ingresos_mes || 0)}</div>
                <div className="stat-label" style={{ color: "rgba(255,255,255,0.8)" }}>Ingresos mes</div>
              </div>
              <div className="card card-elevated stat-card" style={{ background: "var(--error)", color: "white" }}>
                <div className="stat-value">{formatCurrency(datosGestora?.gastos_mes || 0)}</div>
                <div className="stat-label" style={{ color: "rgba(255,255,255,0.8)" }}>Gastos mes</div>
              </div>
              <div className="card card-elevated stat-card" style={{ background: "var(--brand-primary)", color: "white" }}>
                <div className="stat-value">{formatCurrency(datosGestora?.beneficio_mes || 0)}</div>
                <div className="stat-label" style={{ color: "rgba(255,255,255,0.8)" }}>Beneficio neto</div>
              </div>
              <div className="card card-elevated stat-card">
                <div className="stat-value" style={{ color: "var(--brand-accent)" }}>
                  {datosGestora?.rentabilidad_mes || 0}%
                </div>
                <div className="stat-label">Rentabilidad</div>
              </div>
            </div>

            {/* Asistente IA */}
            <div className="card card-accent p-5 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <Brain size={28} weight="fill" />
                <div>
                  <div className="font-bold">Asistente Gestor IA</div>
                  <div className="text-tiny" style={{ opacity: 0.8 }}>Pregunta lo que necesites</div>
                </div>
              </div>
              <Button
                variant="primary"
                className="btn-full"
                onClick={() => setModalIA(true)}
                icon={<Lightning size={20} weight="fill" />}
              >
                Consultar Gestor IA
              </Button>
            </div>

            {/* Alertas pendientes */}
            <div className="card p-4 mb-4">
              <div className="font-bold mb-3 flex items-center gap-2">
                <Bell size={20} />
                Alertas Pendientes ({datosGestora?.alertas?.length || 0})
              </div>
              {datosGestora?.alertas?.slice(0, 5).map((alerta, i) => (
                <div key={i} className="list-item">
                  <div className="list-item-icon" style={{ background: alerta.urgente ? "var(--error)" : "var(--warning)" }}>
                    <Warning size={20} style={{ color: "white" }} weight="fill" />
                  </div>
                  <div className="list-item-content">
                    <div className="list-item-title">{alerta.titulo}</div>
                    <div className="list-item-subtitle">{alerta.descripcion}</div>
                  </div>
                  <Badge variant={alerta.urgente ? "error" : "warning"}>
                    {alerta.urgente ? "Urgente" : "Pendiente"}
                  </Badge>
                </div>
              )) || (
                <EmptyState
                  icon="✅"
                  title="Todo al día"
                  description="No hay alertas pendientes"
                />
              )}
            </div>
          </motion.div>
        )}

        {/* Fiscal */}
        {activeTab === "fiscal" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="card p-3 mb-4" style={{ background: "var(--warning-bg, #FFF8E1)", border: "1px solid var(--warning, #F59E0B)" }}>
              <div className="text-tiny" style={{ color: "#92400E" }}>
                ⚠️ Estos números son una estimación calculada con las facturas y gastos que cargaste en la app.
                No reemplazan a tu gestor ni al Modelo 303 oficial (se presenta en la Sede Electrónica de la AEAT).
              </div>
            </div>

            <div className="card p-5 mb-4">
              <div className="font-bold mb-4 flex items-center gap-2">
                <Calculator size={20} />
                IVA del trimestre
              </div>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <select className="input" value={trimestreSel} onChange={(e) => setTrimestreSel(Number(e.target.value))}>
                  <option value={1}>1º Trimestre (ene-mar)</option>
                  <option value={2}>2º Trimestre (abr-jun)</option>
                  <option value={3}>3º Trimestre (jul-sep)</option>
                  <option value={4}>4º Trimestre (oct-dic)</option>
                </select>
                <select className="input" value={añoSel} onChange={(e) => setAñoSel(Number(e.target.value))}>
                  {[añoSel, añoSel - 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="text-tiny text-secondary mb-3">
                El resumen de abajo (dashboard) es del año en curso completo. Para un trimestre puntual, descargalo.
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-secondary">IVA Repercutido (año, facturas emitidas)</span>
                  <span className="font-bold">{formatCurrency(datosGestora?.iva_repercutido || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary">IVA Soportado (año, gastos deducibles cargados)</span>
                  <span className="font-bold" style={{ color: "var(--success)" }}>-{formatCurrency(datosGestora?.iva_soportado || 0)}</span>
                </div>
                <div className="divider" />
                <div className="flex justify-between text-h3 font-display">
                  <span>A pagar/devolver (año)</span>
                  <span style={{ color: "var(--brand-accent)" }}>
                    {formatCurrency((datosGestora?.iva_repercutido || 0) - (datosGestora?.iva_soportado || 0))}
                  </span>
                </div>
              </div>
              <Button variant="primary" className="btn-full mt-4" onClick={async () => {
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gestora/modelo-303?año=${añoSel}&trimestre=${trimestreSel}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` }
                  });

                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Resumen_IVA_T${trimestreSel}_${añoSel}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  } else {
                    alert("Error al generar el resumen");
                  }
                } catch (e) {
                  console.error(e);
                  alert("Error al generar el resumen");
                }
              }}>
                <Download size={20} /> Descargar resumen del trimestre (para tu gestor)
              </Button>
            </div>

            <div className="card p-5 mb-4">
              <div className="font-bold mb-4 flex items-center gap-2">
                <Receipt size={20} />
                IRPF Estimado (año en curso)
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-secondary">Ingresos anuales (base)</span>
                  <span className="font-bold">{formatCurrency(datosGestora?.ingresos_año || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary">Gastos deducibles (base)</span>
                  <span className="font-bold" style={{ color: "var(--success)" }}>-{formatCurrency(datosGestora?.gastos_deducibles_año || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary">Base imponible</span>
                  <span className="font-bold">{formatCurrency((datosGestora?.ingresos_año || 0) - (datosGestora?.gastos_deducibles_año || 0))}</span>
                </div>
                <div className="flex justify-between text-h3 font-display" style={{ marginTop: 12, paddingTop: 12, borderTop: "2px solid var(--border-subtle)" }}>
                  <span>IRPF estimado (tramos 2025)</span>
                  <span style={{ color: "var(--brand-accent)" }}>
                    {formatCurrency(calcularIrpfProgresivo((datosGestora?.ingresos_año || 0) - (datosGestora?.gastos_deducibles_año || 0)))}
                  </span>
                </div>
                <div className="text-tiny text-secondary mt-2">
                  Calculado con los tramos generales combinados 2025 (varían según tu comunidad autónoma). No incluye retenciones ya pagadas ni pagos fraccionados. Consultá con tu gestor el importe exacto.
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Alertas */}
        {activeTab === "alertas" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {loadingAlertas ? (
              <Loading />
            ) : alertas.length === 0 ? (
              <div className="card p-6">
                <div style={{ textAlign: "center" }}>
                  <Bell size={48} style={{ color: "var(--brand-accent)", margin: "0 auto 16px" }} />
                  <div className="text-h3 mb-2">Todo al día</div>
                  <div className="text-small text-secondary mb-4">
                    Te avisamos automáticamente sobre presupuestos sin responder hace más
                    de 7 días y facturas sin cobrar hace más de 30.
                  </div>
                  <div className="mt-2 p-3 rounded" style={{ background: "var(--brand-accent-bg)" }}>
                    <div className="text-tiny" style={{ color: "var(--brand-accent)" }}>
                      ✅ No tenés alertas pendientes ahora mismo.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card">
                {alertas.map((a, i) => (
                  <div key={i} className="list-item">
                    <div className="list-item-icon" style={{ background: a.prioridad === "alta" ? "var(--error)" : "var(--warning)" }}>
                      <Warning size={20} style={{ color: "white" }} weight="fill" />
                    </div>
                    <div className="list-item-content">
                      <div className="list-item-title">{a.titulo}</div>
                      <div className="list-item-subtitle">{a.descripcion}</div>
                    </div>
                    <Badge variant={a.prioridad === "alta" ? "error" : "warning"}>
                      {a.prioridad === "alta" ? "Urgente" : "Media"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {datosGestora?.recordatorios?.length > 0 && (
              <div className="card p-4 mt-4">
                <div className="font-bold mb-3 flex items-center gap-2">
                  <Calendar size={18} /> Próximos eventos en tu agenda
                </div>
                {datosGestora.recordatorios.map((rec, i) => (
                  <div key={i} className="list-item">
                    <div className="list-item-content">
                      <div className="list-item-title">{rec.titulo}</div>
                      <div className="list-item-subtitle">{formatDate(rec.fecha)}{rec.hora ? ` · ${rec.hora}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Gastos */}
        {activeTab === "gastos" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="card p-4 mb-4">
              <div className="font-bold mb-3">Registrar gasto real</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="date"
                  className="input"
                  value={nuevoGasto.fecha}
                  onChange={(e) => setNuevoGasto((p) => ({ ...p, fecha: e.target.value }))}
                />
                <select
                  className="input"
                  value={nuevoGasto.categoria}
                  onChange={(e) => setNuevoGasto((p) => ({ ...p, categoria: e.target.value }))}
                >
                  <option value="materiales">Materiales</option>
                  <option value="combustible">Combustible</option>
                  <option value="herramientas">Herramientas</option>
                  <option value="gestor">Gestor/Asesoría</option>
                  <option value="seguro">Seguro</option>
                  <option value="alquiler">Alquiler/Local</option>
                  <option value="telefono">Teléfono/Internet</option>
                  <option value="otros">Otros</option>
                </select>
              </div>
              <input
                type="text"
                className="input mb-2"
                placeholder="Descripción (ej: Gasoil furgoneta agosto)"
                value={nuevoGasto.descripcion}
                onChange={(e) => setNuevoGasto((p) => ({ ...p, descripcion: e.target.value }))}
              />
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="number"
                  className="input"
                  placeholder="Importe total (€, con IVA)"
                  value={nuevoGasto.importe}
                  onChange={(e) => setNuevoGasto((p) => ({ ...p, importe: e.target.value }))}
                />
                <select
                  className="input"
                  value={nuevoGasto.iva_porcentaje}
                  onChange={(e) => setNuevoGasto((p) => ({ ...p, iva_porcentaje: Number(e.target.value) }))}
                >
                  <option value={21}>IVA 21%</option>
                  <option value={10}>IVA 10%</option>
                  <option value={4}>IVA 4%</option>
                  <option value={0}>Sin IVA</option>
                </select>
              </div>
              <label className="flex items-center gap-2 mb-3 text-small">
                <input
                  type="checkbox"
                  checked={nuevoGasto.deducible}
                  onChange={(e) => setNuevoGasto((p) => ({ ...p, deducible: e.target.checked }))}
                />
                Deducible (cuenta para IVA soportado / gastos del negocio)
              </label>
              <Button variant="accent" className="btn-full" onClick={guardarGasto} icon={<Plus size={18} />}>
                Guardar gasto
              </Button>
            </div>

            {loadingGastos ? (
              <Loading />
            ) : gastos.length === 0 ? (
              <EmptyState icon="💸" title="Sin gastos cargados" description="Registrá tus gastos reales para que el Panel y el resumen fiscal sean exactos" />
            ) : (
              <div className="card">
                {gastos.map((g) => (
                  <div key={g.id} className="list-item">
                    <div className="list-item-content">
                      <div className="list-item-title">{g.descripcion}</div>
                      <div className="list-item-subtitle">
                        {formatDate(g.fecha)} · {g.categoria} · {g.deducible ? "Deducible" : "No deducible"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{formatCurrency(g.importe)}</div>
                      <Button variant="ghost" size="sm" onClick={() => eliminarGasto(g.id)} icon={<Trash size={14} />} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Facturas */}
        {activeTab === "facturas" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Button 
              variant="accent" 
              className="btn-full mb-4" 
              icon={<FileText size={20} />}
              onClick={() => onNavigate && onNavigate('facturas')}
            >
              Ir a Gestión de Facturas
            </Button>
            <div className="card">
              {datosGestora?.facturas?.map((fac, i) => (
                <div
                  key={i}
                  className="list-item list-item-interactive"
                  onClick={() => onNavigate && onNavigate('facturas')}
                >
                  <div className="list-item-content">
                    <div className="list-item-title">{fac.numero}</div>
                    <div className="list-item-subtitle">{fac.cliente} • {formatDate(fac.fecha)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{formatCurrency(fac.total)}</div>
                    <Badge variant={fac.cobrada ? "success" : "warning"}>{fac.cobrada ? "Cobrada" : "Pendiente"}</Badge>
                  </div>
                </div>
              )) || (
                <EmptyState
                  icon="📄"
                  title="Sin facturas"
                  description="Genera facturas desde presupuestos"
                />
              )}
            </div>
          </motion.div>
        )}

        {/* Cobros */}
        {activeTab === "cobros" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-tiny text-secondary mb-3">
              Presupuestos aceptados o en curso, ordenados por lo que más te deben.
            </div>
            {loadingCobros ? (
              <Loading />
            ) : cobros.length === 0 ? (
              <EmptyState icon="💵" title="Sin cobros pendientes" description="Cuando aceptes un presupuesto vas a poder registrar sus pagos aquí" />
            ) : (
              <div className="card">
                {cobros.map((c) => (
                  <div key={c.presupuesto_id} className="list-item list-item-interactive" onClick={() => abrirCobro(c)}>
                    <div className="list-item-content">
                      <div className="list-item-title">{c.cliente_nombre}</div>
                      <div className="list-item-subtitle">{c.numero} · {c.cliente_telefono}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold" style={{ color: c.pendiente > 0 ? "var(--error)" : "var(--success)" }}>
                        {c.pendiente > 0 ? `Debe ${formatCurrency(c.pendiente)}` : "Pagado"}
                      </div>
                      <div className="text-tiny text-secondary">{formatCurrency(c.total_pagado)} de {formatCurrency(c.total)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Informes */}
        {activeTab === "informes" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-tiny text-secondary mb-3">
              Todos calculados con facturas reales emitidas y gastos que cargaste, año en curso.
            </div>
            <div className="space-y-3">
              {[
                { tipo: "cobros", titulo: "Cuentas por Cobrar", desc: "Facturas emitidas y aún no cobradas" },
                { tipo: "resultados", titulo: "Cuenta de Resultados", desc: "Ingresos y gastos del período" },
                { tipo: "rentabilidad", titulo: "Análisis de Rentabilidad", desc: "Sobre facturación real" },
                { tipo: "fiscal", titulo: "Resumen Fiscal", desc: "IVA e IRPF estimado" },
                { tipo: "tesoreria", titulo: "Flujo de Tesorería", desc: "Cobrado vs. gastado" },
              ].map((informe, i) => (
                <div key={i} className="card p-4 card-interactive" onClick={() => generarInforme(informe.tipo)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-small">{informe.titulo}</div>
                      <div className="text-tiny text-secondary">{informe.desc}</div>
                    </div>
                    <Download size={24} style={{ color: "var(--brand-accent)" }} />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Modal Cobro */}
      <Modal
        isOpen={!!cobroSeleccionado}
        onClose={() => setCobroSeleccionado(null)}
        title={cobroSeleccionado ? `💵 ${cobroSeleccionado.numero}` : ""}
      >
        {cobroSeleccionado && (
          <div>
            <div className="card p-3 mb-4" style={{ background: "var(--surface-secondary)" }}>
              <div className="flex justify-between text-small mb-1">
                <span className="text-secondary">Total presupuesto</span>
                <span className="font-bold">{formatCurrency(cobroSeleccionado.total)}</span>
              </div>
              <div className="flex justify-between text-small mb-1">
                <span className="text-secondary">Pagado</span>
                <span className="font-bold" style={{ color: "var(--success)" }}>{formatCurrency(cobroSeleccionado.total_pagado)}</span>
              </div>
              <div className="flex justify-between text-small" style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 6, marginTop: 4 }}>
                <span className="font-bold">Pendiente</span>
                <span className="font-bold" style={{ color: cobroSeleccionado.pendiente > 0 ? "var(--error)" : "var(--success)" }}>
                  {formatCurrency(cobroSeleccionado.pendiente)}
                </span>
              </div>
              <div className="text-tiny text-secondary mt-2">
                Plan: anticipo {formatCurrency(cobroSeleccionado.anticipo_importe)} · mitad obra {formatCurrency(cobroSeleccionado.mitad_obra_importe)} · resto {formatCurrency(cobroSeleccionado.resto_importe)}
              </div>
            </div>

            <Button variant="whatsapp" className="btn-full mb-4" onClick={() => avisarPagoWhatsApp(cobroSeleccionado)} icon={<Lightning size={18} />}>
              Avisar por WhatsApp
            </Button>

            {cobroSeleccionado.tipo_presupuesto === "mo_material" && (
              <div className="card p-3 mb-4" style={{ background: "var(--surface-secondary)" }}>
                <div className="font-bold mb-1">Costo real de materiales</div>
                <div className="text-tiny text-secondary mb-3">
                  Estimado en el presupuesto: {formatCurrency(cobroSeleccionado.total_materiales)}.
                  Si gastaste menos comprando, registralo acá y se descuenta del total del cliente — no toca tus precios generales.
                </div>

                {cobroSeleccionado.ajuste_materiales_aplicado && (
                  <div className="text-small mb-3" style={{ background: "#F0FDF4", padding: 10, borderRadius: 8, color: "#065F46" }}>
                    ✅ Ya registrado: gastaste {formatCurrency(cobroSeleccionado.materiales_costo_real)}
                    {cobroSeleccionado.ahorro_materiales > 0 && ` · ahorro de ${formatCurrency(cobroSeleccionado.ahorro_materiales)} trasladado al cliente`}
                  </div>
                )}

                <input
                  type="number"
                  className="input mb-2"
                  placeholder="Costo real total de materiales (€)"
                  value={costoRealMateriales}
                  onChange={(e) => setCostoRealMateriales(e.target.value)}
                />
                <input
                  type="text"
                  className="input mb-2"
                  placeholder="Nota interna (opcional, ej: oferta en Leroy Merlin)"
                  value={notaAjusteMateriales}
                  onChange={(e) => setNotaAjusteMateriales(e.target.value)}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={registrarAjusteMateriales}>
                    Registrar y ajustar
                  </Button>
                  <Button
                    variant="whatsapp"
                    onClick={() => avisarAjusteWhatsApp(cobroSeleccionado)}
                    disabled={!cobroSeleccionado.ajuste_materiales_aplicado}
                  >
                    Avisar ajuste
                  </Button>
                </div>
              </div>
            )}

            <div className="font-bold mb-2">Registrar pago</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="date"
                className="input"
                value={nuevoPago.fecha}
                onChange={(e) => setNuevoPago((p) => ({ ...p, fecha: e.target.value }))}
              />
              <select
                className="input"
                value={nuevoPago.concepto}
                onChange={(e) => setNuevoPago((p) => ({ ...p, concepto: e.target.value }))}
              >
                <option value="anticipo">Anticipo</option>
                <option value="mitad_obra">Mitad de obra</option>
                <option value="final">Pago final</option>
                <option value="extra">Extra</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="number"
                className="input"
                placeholder="Importe (€)"
                value={nuevoPago.importe}
                onChange={(e) => setNuevoPago((p) => ({ ...p, importe: e.target.value }))}
              />
              <select
                className="input"
                value={nuevoPago.metodo}
                onChange={(e) => setNuevoPago((p) => ({ ...p, metodo: e.target.value }))}
              >
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="bizum">Bizum</option>
                <option value="tarjeta">Tarjeta</option>
              </select>
            </div>
            <Button variant="accent" className="btn-full" onClick={registrarPago}>
              Guardar pago
            </Button>
          </div>
        )}
      </Modal>

      {/* Modal IA */}
      <Modal
        isOpen={modalIA}
        onClose={() => {
          setModalIA(false);
          setPreguntaIA("");
          setRespuestaIA("");
        }}
        title="🤖 Gestor IA"
      >
        <div>
          <Textarea
            label="Pregunta al gestor"
            placeholder="Ej: ¿Cuánto debo declarar este trimestre de IVA?"
            value={preguntaIA}
            onChange={(e) => setPreguntaIA(e.target.value)}
            rows={3}
          />
          <Button
            variant="accent"
            className="btn-full mb-4"
            onClick={consultarIA}
            disabled={loadingIA || !preguntaIA.trim()}
          >
            {loadingIA ? "Consultando..." : "Consultar"}
          </Button>

          {respuestaIA && (
            <div className="card p-4" style={{ background: "var(--surface-secondary)" }}>
              <div className="text-label mb-2" style={{ color: "var(--brand-primary)" }}>RESPUESTA</div>
              <div className="text-small" style={{ whiteSpace: "pre-wrap" }}>{respuestaIA}</div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
